import { useId, type ReactNode } from 'react';

/**
 * A labelled form field.
 *
 * Accessibility is structural rather than bolted on: a real `<label for>`, hints and
 * errors wired through `aria-describedby`, `aria-invalid` on the control, and the error
 * announced politely as it appears. Errors are text — never a colour change alone.
 */

interface FieldProps {
  label: string;
  hint?: string;
  error?: string | undefined;
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
  }) => ReactNode;
}

export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children({
        id,
        'aria-describedby': describedBy || undefined,
        'aria-invalid': error ? true : undefined,
      })}
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      <span className="field__error" id={errorId} role="alert">
        {error ?? ''}
      </span>
    </div>
  );
}
